import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
// @mui
import {
    Card,
    Container,
    Box,
    CircularProgress,
    Typography,
    Grid,
    Stack,
    Divider,
    CardHeader,
    Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
// routes
import { PATH_MASTERDATA } from 'src/routes/paths';
// components
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import { useSettingsContext } from 'src/components/settings';
import ProfileCover from 'src/sections/@dashboard/user/profile/ProfileCover';
import SiteCourierList from 'src/sections/@dashboard/site/SiteCourierList';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
// utils
import { fNumber } from 'src/utils/formatNumber';
import { fDate } from 'src/utils/formatTime';
// api
import siteAPI from 'src/api/siteAPI';

// ----------------------------------------------------------------------

const StyledIcon = styled(Iconify)(({ theme }) => ({
    width: 20,
    height: 20,
    marginTop: 1,
    flexShrink: 0,
    marginRight: theme.spacing(2),
}));

// ----------------------------------------------------------------------

export default function SiteDetailPage() {
    const { themeStretch } = useSettingsContext();
    const { enqueueSnackbar } = useSnackbar();
    const { id } = useParams();

    const [site, setSite] = useState(null);
    const [loading, setLoading] = useState(true);

    // 🔹 Fetch site detail dari backend
    useEffect(() => {
        const fetchSite = async () => {
            try {
                setLoading(true);
                const response = await siteAPI.getSiteById(id);
                setSite(response.data);
            } catch (error) {
                console.error(error);
                enqueueSnackbar('Failed to load site details', { variant: 'error' });
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchSite();
    }, [id, enqueueSnackbar]);

    if (loading) {
        return (
            <Container
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '70vh',
                    flexDirection: 'column',
                }}
            >
                <CircularProgress color="primary" />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                    Loading site detail...
                </Typography>
            </Container>
        );
    }

    if (!site) {
        return (
            <Container>
                <Typography variant="h6" color="text.secondary">
                    Site not found.
                </Typography>
            </Container>
        );
    }

    return (
        <>
            <Helmet>
                <title>Site Detail | Deposit Monitoring System</title>
            </Helmet>

            <Container maxWidth={themeStretch ? false : 'lg'}>
                <CustomBreadcrumbs
                    heading="Site Detail"
                    links={[
                        { name: 'Master Data', href: PATH_MASTERDATA.root },
                        { name: 'Site List', href: PATH_MASTERDATA.site },
                        { name: site.name },
                    ]}
                />

                {/* ✅ Cover Info */}
                <Card
                    sx={{
                        mb: 3,
                        height: 200,
                        position: 'relative',
                    }}
                >
                    <ProfileCover
                        name={site.name}
                        role="Site"
                        cover={site.location || '-'}
                    />
                </Card>

                {/* ✅ Main Content */}
                <Grid container spacing={3}>
                    {/* 🔹 Left Side */}
                    <Grid item xs={12} md={4}>
                        <Stack spacing={3}>
                            {/* Total Deposit + Total Courier */}
                            <Card sx={{ py: 3 }}>
                                <Stack direction="row" divider={<Divider orientation="vertical" flexItem />}>
                                    <Stack width={1} textAlign="center">
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            Total Deposit
                                        </Typography>
                                        <Typography variant="h4">{fNumber(site.total_deposit || 0)}</Typography>
                                    </Stack>

                                    <Stack width={1} textAlign="center">
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            Total Courier
                                        </Typography>
                                        <Typography variant="h4">{fNumber(site.total_courier || 0)}</Typography>
                                    </Stack>
                                </Stack>
                            </Card>

                            {/* Site Information */}
                            <Card>
                                <CardHeader title="Site Information" />

                                <Stack spacing={2} sx={{ p: 3 }}>
                                    <Stack direction="row" alignItems="center">
                                        <StyledIcon icon="eva:home-fill" />
                                        <Typography variant="body2">
                                            Name: <strong>{site.name || '-'}</strong>
                                        </Typography>
                                    </Stack>

                                    <Stack direction="row" alignItems="center">
                                        <StyledIcon icon="eva:pin-fill" />
                                        <Typography variant="body2">
                                            Location: <strong>{site.location || '-'}</strong>
                                        </Typography>
                                    </Stack>

                                    <Divider />

                                    <Stack direction="row" alignItems="center">
                                        <StyledIcon icon="eva:credit-card-fill" />
                                        <Typography variant="body2">
                                            Payroll: <strong>{site.is_payroll ? '2x' : '1x'}</strong>
                                        </Typography>
                                    </Stack>

                                    <Divider />

                                    <Stack direction="row" spacing={1}>
                                        <Chip
                                            label={site.is_active ? 'Active' : 'Inactive'}
                                            color={site.is_active ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </Stack>

                                    <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                                        Last updated on {fDate(site.updated_at)}
                                    </Typography>
                                </Stack>
                            </Card>
                        </Stack>
                    </Grid>

                    {/* 🔹 Right Side */}
                    <Grid item xs={12} md={8}>
                        <Stack spacing={3}>
                            <SiteCourierList siteId={id} />
                        </Stack>
                    </Grid>
                </Grid>
            </Container>
        </>
    );
}
